import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { appErrorCode, NetworkError } from '../lib/api';
import { SESSION_QUERY_KEY } from '../lib/auth';
import { NAV_BADGES_QUERY_KEY } from '../lib/nav-badges';
import { createTrpcClient, trpc } from '../lib/trpc';
import { Toaster } from '../ui/Toast';
import { TooltipProvider } from '../ui/Tooltip';
import { StepUpDialog } from './StepUpDialog';

/** Błędy, których ponawianie nie ma sensu (decyzja serwera, nie chwilowa awaria). */
const NO_RETRY = new Set([
  'VALIDATION_ERROR',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'STEP_UP_REQUIRED',
  'RATE_LIMITED',
]);

export function createQueryClient(): QueryClient {
  // Wygasła sesja w dowolnym zapytaniu → odśwież sesję, a strażnik tras przekieruje na /login.
  const onError = (error: unknown) => {
    if (appErrorCode(error) === 'UNAUTHENTICATED') {
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    }
  };
  // Po każdej udanej zmianie: plakietki menu (Inbox, Akceptacje) i ekran Dziś (kafelki, „Wymaga Ciebie”) — zmiana
  // w dowolnym module może dodać albo zamknąć akceptację lub zdarzenie (kompozycja Fali 3). Odświeżają się tylko
  // zapytania na ekranie; reszta jest oznaczona jako nieaktualna.
  const onSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: NAV_BADGES_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['today'] });
  };
  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError, onSuccess }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          const code = appErrorCode(error);
          if (code && NO_RETRY.has(code)) return false;
          return failureCount < (error instanceof NetworkError ? 2 : 1);
        },
      },
      // offline: mutacja ma się od razu nie udać (lib/offline.ts), a nie czekać i wykonać się sama po powrocie sieci
      mutations: { retry: false, networkMode: 'always' },
    },
  });
  return queryClient;
}

export function AppProviders({ children, queryClient: provided }: { children: ReactNode; queryClient?: QueryClient }) {
  const [queryClient] = useState(() => provided ?? createQueryClient());
  const [trpcClient] = useState(() => createTrpcClient());
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={400} skipDelayDuration={200}>
          {children}
          <StepUpDialog />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
}
